'use client';
import React from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { ArrowLeft, Users } from 'lucide-react';

const sections = [
  {
    heading: 'Our Principles',
    content: 'Panorama Towers is a private community. The standards here reflect the kind of neighbor-to-neighbor respect we expect in the building itself. This platform exists because residents wanted it — and it stays valuable because residents maintain it.',
  },
  {
    heading: 'Respect & Conduct',
    content: 'Every interaction on this platform is with a neighbor. Treat others accordingly.',
    list: [
      'Be respectful in all interactions — disagreements happen, but personal attacks do not',
      'Do not harass, threaten, or intimidate other residents',
      'Do not post content that is discriminatory based on race, gender, religion, nationality, sexual orientation, or any other protected characteristic',
      'Constructive criticism is welcome; targeted hostility is not',
      'If you have a dispute with a neighbor, handle it privately or through appropriate channels',
    ],
  },
  {
    heading: 'Listings & Services',
    content: 'The marketplace and services directory are for genuine resident-to-resident exchange.',
    list: [
      'Keep listings and service descriptions accurate and honest',
      'Do not post items or services you cannot actually provide',
      'Pricing should be fair and clearly stated',
      'Do not use the marketplace for commercial solicitation outside the designated services area',
      'Wanted posts should be genuine requests, not fishing for contact information',
    ],
  },
  {
    heading: 'Reviews',
    content: 'Reviews are a trust mechanism for the community. They must be genuine.',
    list: [
      'Reviews must be based on real, personal experiences',
      'Do not post fake positive reviews for yourself or others',
      'Do not post retaliatory or malicious reviews',
      'Reviews are from verified residents only — no anonymous reviews',
      'Providers do not reply to reviews on this platform',
    ],
  },
  {
    heading: 'Privacy',
    content: 'Resident privacy is a core value of this platform.',
    list: [
      'Do not share other residents\' contact information without their explicit consent',
      'Do not post photos of other residents without their consent',
      'Do not share private conversations or messages publicly',
      'Respect residents who choose to limit their profile visibility',
      'Do not attempt to identify or expose anonymous or private residents',
    ],
  },
  {
    heading: 'Conversations',
    content: 'The conversations section is for community discussion — questions, recommendations, alerts, and shared interests.',
    list: [
      'Stay on topic within threads',
      'Do not post spam, promotional content, or off-topic solicitation',
      'Political and divisive content should be handled with care — this is a community space, not a debate forum',
      'Alerts should be genuine and relevant to the community',
      'Do not post content that could create unnecessary alarm or panic',
    ],
  },
  {
    heading: 'Moderation',
    content: 'Content that violates these guidelines may be removed without notice. Repeated violations may result in account suspension or permanent removal from the platform. Moderation decisions are made by the platform team, not building management. Reports are reviewed confidentially.',
  },
  {
    heading: 'Reporting',
    content: 'If you see content that violates these guidelines, use the report function on any post, listing, or profile. Reports are confidential and reviewed by the platform team. Do not attempt to moderate content yourself — use the report function.',
  },
  {
    heading: 'Changes',
    content: 'These guidelines may be updated as the community grows and evolves. Significant changes will be communicated to verified residents. The spirit of these guidelines — respect, honesty, and privacy — will not change.',
  },
];

export default function CommunityGuidelinesPage() {
  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        {/* Back nav */}
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-3xl mx-auto px-6 lg:px-10 py-4">
            <Link href="/resources" className="inline-flex items-center gap-2 text-[13px] transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
              <ArrowLeft size={14} /> Back to Resources
            </Link>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
          {/* Header */}
          <div className="flex items-start gap-4 mb-10">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(122,92,255,0.10)', border: '1px solid rgba(122,92,255,0.20)' }}
            >
              <Users size={20} style={{ color: '#9B7FFF' }} />
            </div>
            <div>
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide mb-2"
                style={{ background: 'rgba(122,92,255,0.10)', border: '1px solid rgba(122,92,255,0.20)', color: '#9B7FFF' }}
              >
                Community
              </span>
              <h1 className="font-display font-bold text-[32px] sm:text-[38px] leading-tight" style={{ color: '#F2F4FF' }}>
                Community Guidelines
              </h1>
              <p className="text-[13px] mt-2" style={{ color: 'rgba(167,163,196,0.55)' }}>
                Last updated: March 2026 · Panorama Community Network
              </p>
            </div>
          </div>

          {/* Intro note */}
          <div
            className="px-5 py-4 rounded-xl mb-10"
            style={{ background: 'rgba(122,92,255,0.05)', border: '1px solid rgba(122,92,255,0.15)' }}
          >
            <p className="text-[13.5px] leading-relaxed" style={{ color: 'rgba(242,244,255,0.75)' }}>
              These guidelines exist to keep the Panorama community platform respectful, useful, and trustworthy for all residents. They apply to all content and interactions on the platform.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {sections?.map((section, idx) => (
              <div key={idx}>
                <h2 className="font-display font-semibold text-[18px] mb-3" style={{ color: '#F2F4FF' }}>
                  {section?.heading}
                </h2>
                {section?.content && (
                  <p className="text-[14.5px] leading-relaxed mb-3" style={{ color: '#A7A3C4' }}>
                    {section?.content}
                  </p>
                )}
                {section?.list && (
                  <ul className="space-y-2 mt-3">
                    {section?.list?.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: '#9B7FFF' }} />
                        <span className="text-[14px] leading-relaxed" style={{ color: '#A7A3C4' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {idx < sections?.length - 1 && (
                  <div className="neon-divider mt-10" />
                )}
              </div>
            ))}
          </div>

          {/* Footer links */}
          <div
            className="mt-14 pt-8 flex flex-wrap gap-4"
            style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
          >
            <Link href="/privacy-policy" className="text-[13px] font-medium transition-colors duration-150 hover:opacity-80" style={{ color: '#4CC9FF' }}>
              Privacy Policy →
            </Link>
            <Link href="/terms-of-use" className="text-[13px] font-medium transition-colors duration-150 hover:opacity-80" style={{ color: '#4CC9FF' }}>
              Terms of Use →
            </Link>
            <Link href="/resources/community-guidelines" className="text-[13px] font-medium transition-colors duration-150 hover:opacity-80" style={{ color: '#4CC9FF' }}>
              Community Guidelines Resource →
            </Link>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
